import { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Button, Tabs, Tab, Badge, Spinner, Modal } from 'react-bootstrap';
import { useParams, useNavigate } from 'react-router-dom';
import { FaEdit, FaPrint, FaDownload, FaExchangeAlt } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { getEmployee, updateEmployeeStatus } from '../services/firebase/employeeService';
import { useAuth } from '../context/AuthContext';
import Navbar from '../components/common/Navbar';
import Sidebar from '../components/common/Sidebar';
import PersonalDetailsSection from '../components/employee/PersonalDetailsSection';
import JobDetailsSection from '../components/employee/JobDetailsSection';
import BankDetailsSection from '../components/employee/BankDetailsSection';
import SalaryStructureSection from '../components/employee/SalaryStructureSection';
import DocumentsSection from '../components/employee/DocumentsSection';
import AttendanceSummarySection from '../components/employee/AttendanceSummarySection';
import LeaveBalanceSection from '../components/employee/LeaveBalanceSection';
import CareerGrowthSection from '../components/employee/CareerGrowthSection';
import '../assets/css/employee.css';

const EmployeeProfilePage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isHROrAdmin = user?.role === 'hr' || user?.role === 'admin';

  const [employee, setEmployee] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [statusLoading, setStatusLoading] = useState(false);



  // Document Preview State
  const [previewConfig, setPreviewConfig] = useState({
    show: false,
    title: '',
    url: ''
  });

  const handlePreview = (url, title) => {
    setPreviewConfig({
      show: true,
      title: title,
      url: url
    });
  };

  useEffect(() => {
    fetchEmployee();
  }, [id]);

  const fetchEmployee = async () => {
    setLoading(true);
    try {
      const response = await getEmployee(id);

      if (response.success) {
        console.log('Employee Data Loaded:', response.data);
        setEmployee(response.data);
      }
    } catch (error) {
      console.error('Error fetching employee:', error);
      toast.error('Failed to fetch employee details');
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const handleDownloadPDF = () => {
    toast.info('PDF download feature coming soon');
  };

  const handleStatusChange = async () => {
    setStatusLoading(true);
    try {
      const newStatus = employee.status === 'Active' ? 'Inactive' : 'Active';
      const response = await updateEmployeeStatus(id, newStatus, employee.personal?.email);
      
      if (response.success) {
        toast.success(`Employee ${newStatus === 'Active' ? 'activated' : 'deactivated'} successfully`);
        setShowStatusModal(false);
        fetchEmployee();
      }
    } catch (error) {
      console.error('Error changing status:', error);
      toast.error(error.message || 'Failed to change employee status');
    } finally {
      setStatusLoading(false);
    }
  };



  const getStatusBadge = (status) => {
    const variants = {
      'Active': 'success',
      'Inactive': 'danger',
      'On Leave': 'warning',
      'Probation': 'info'
    };
    return <Badge bg={variants[status] || 'secondary'} className="fs-6">{status}</Badge>;
  };

  if (loading) {
    return (
      <div className="d-flex" style={{ width: '100%', overflowX: 'hidden' }}>
        <Sidebar />
        <div className="flex-grow-1 profile-main-content">
          <Navbar />
          <Container fluid className="py-3 py-md-4 px-2 px-md-4">
            <div className="text-center py-5">
              <Spinner animation="border" variant="primary" />
              <p className="mt-2">Loading employee profile...</p>
            </div>
          </Container>
        </div>
      </div>
    );
  }

  if (!employee) {
    return (
      <div className="d-flex" style={{ width: '100%', overflowX: 'hidden' }}>
        <Sidebar />
        <div className="flex-grow-1 profile-main-content">
          <Navbar />
          <Container fluid className="py-3 py-md-4 px-2 px-md-4">
            <div className="text-center py-5">
              <h4>Employee not found</h4>
              <Button variant="primary" onClick={() => navigate('/employees')}>
                Back to Employee List
              </Button>
            </div>
          </Container>
        </div>
      </div>
    );
  }

  return (
    <div className="d-flex" style={{ width: '100%', overflowX: 'hidden' }}>
      <Sidebar />
      <div className="flex-grow-1 profile-main-content">
        <Navbar />
        <Container fluid className="py-3 py-md-4 px-2 px-md-4 profile-content">
          {/* Profile Header */}
          <Card className="mb-3 mb-md-4 shadow-sm border-0 rounded-4 overflow-hidden">
            <Card.Body className="p-3 p-md-4" style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: '#fff' }}>
              <Row className="align-items-center g-3">
                <Col xs={12} md={2} className="text-center">
                  {(employee.personal?.photo || employee.photoURL || employee.photo) ? (
                    <img
                      src={employee.personal?.photo || employee.photoURL || employee.photo}
                      alt={employee.personal?.fullName}
                      className="rounded-circle shadow"
                      style={{ width: '100px', height: '100px', objectFit: 'cover', border: '3px solid #fff' }}
                      onError={(e) => { e.currentTarget.style.display = 'none'; }}
                    />
                  ) : (
                    <div
                      className="rounded-circle d-flex align-items-center justify-content-center mx-auto shadow-sm"
                      style={{ 
                        width: '100px', 
                        height: '100px', 
                        backgroundColor: '#e8eaf6', 
                        color: '#1a237e', 
                        fontSize: '2.5rem', 
                        fontWeight: '800', 
                        border: '3px solid #fff' 
                      }}
                    >
                      {(employee.personal?.firstName?.charAt(0) || '') + (employee.personal?.lastName?.charAt(0) || '')}
                    </div>
                  )}
                </Col>
                <Col xs={12} md={7} className="text-center text-md-start">
                  <h2 className="mb-1 fs-4 fs-md-2 fw-bold">{employee.personal?.fullName}</h2>
                  <h6 className="mb-2 opacity-90 fs-6">{employee.job?.designation} - {employee.job?.department}</h6>
                  <div className="mb-2 small opacity-90 d-flex flex-wrap justify-content-center justify-content-md-start gap-2 gap-md-3">
                    <span><strong>Employee ID:</strong> {employee.job?.employeeCode}</span>
                    <span className="d-none d-md-inline">|</span>
                    <span className="text-break"><strong>Email:</strong> {employee.personal?.email}</span>
                  </div>
                  <div className="d-flex flex-wrap justify-content-center justify-content-md-start">
                    {getStatusBadge(employee.status)}
                  </div>
                </Col>
                <Col xs={12} md={3} className="text-center text-md-end mt-2 mt-md-0 d-flex flex-wrap justify-content-center justify-content-md-end gap-2">
                  {isHROrAdmin && (
                    <>
                      <Button
                        variant="light"
                        size="sm"
                        className="fw-bold shadow-sm"
                        onClick={() => navigate(`/employees/${id}/edit`)}
                      >
                        <FaEdit /> Edit
                      </Button>
                      <Button
                        variant="light"
                        size="sm"
                        className="fw-bold shadow-sm"
                        onClick={() => setShowStatusModal(true)}
                      >
                        <FaExchangeAlt /> {employee.status === 'Active' ? 'Deactivate' : 'Activate'}
                      </Button>
                    </>
                  )}
                  <Button variant="light" size="sm" className="fw-bold shadow-sm" onClick={handlePrint}>
                    <FaPrint /> Print
                  </Button>
                  <Button variant="light" size="sm" className="fw-bold shadow-sm" onClick={handleDownloadPDF}>
                    <FaDownload /> PDF
                  </Button>
                </Col>
              </Row>
            </Card.Body>
          </Card>

          {/* Tabs */}
          <Card className="shadow-sm border-0 rounded-4">
            <Card.Body className="p-2 p-md-3">
              <div className="profile-page-tabs mb-3">
                <Tabs activeKey={activeTab} onSelect={(k) => setActiveTab(k)} className="mb-3">
                  <Tab eventKey="overview" title="Overview">
                    <Row className="g-3">
                      <Col xs={12} md={6}>
                        <PersonalDetailsSection 
                          employee={employee} 
                          onPreview={(url) => handlePreview(url, 'Aadhaar Card Scan')}
                        />
                      </Col>
                      <Col xs={12} md={6}>
                        <JobDetailsSection employee={employee} />
                      </Col>
                    </Row>
                    {isHROrAdmin && (
                      <Row className="g-3 mt-1">
                        <Col xs={12} md={6}>
                          <BankDetailsSection 
                            employee={employee} 
                            onPreview={(url) => handlePreview(url, 'Bank Passbook / Cheque Scan')}
                          />
                        </Col>
                        <Col xs={12} md={6}>
                          <SalaryStructureSection employee={employee} />
                        </Col>
                      </Row>
                    )}


                  </Tab>

                  <Tab eventKey="attendance" title="Attendance">
                    <AttendanceSummarySection employeeId={employee?.job?.employeeCode || id} />
                  </Tab>

                  <Tab eventKey="leaves" title="Leaves">
                    <LeaveBalanceSection employeeId={id} />
                  </Tab>

                  <Tab eventKey="documents" title="Documents">
                    <DocumentsSection employee={employee} onRefresh={fetchEmployee} />
                  </Tab>

                  <Tab eventKey="growth" title="Career Growth & Journey">
                    <CareerGrowthSection employee={employee} onRefresh={fetchEmployee} />
                  </Tab>
                </Tabs>
              </div>
            </Card.Body>
          </Card>
        </Container>

        {/* Status Change Confirmation Modal */}
        <Modal show={showStatusModal} onHide={() => setShowStatusModal(false)} centered>
          <Modal.Header closeButton>
            <Modal.Title>
              {employee?.status === 'Active' ? 'Deactivate' : 'Activate'} Employee
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <p>
              Are you sure you want to {employee?.status === 'Active' ? 'deactivate' : 'activate'}{' '}
              <strong>{employee?.personal?.fullName}</strong>?
            </p>
            {employee?.status === 'Active' ? (
              <p className="text-danger mb-0">
                <small>This will disable the employee's login access.</small>
              </p>
            ) : (
              <p className="text-success mb-0">
                <small>This will enable the employee's login access.</small>
              </p>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowStatusModal(false)} disabled={statusLoading}>
              Cancel
            </Button>
            <Button
              variant={employee?.status === 'Active' ? 'danger' : 'success'}
              onClick={handleStatusChange}
              disabled={statusLoading}
            >
              {statusLoading ? (
                <>
                  <Spinner size="sm" className="me-2" />
                  Processing...
                </>
              ) : (
                employee?.status === 'Active' ? 'Deactivate' : 'Activate'
              )}
            </Button>
          </Modal.Footer>
        </Modal>

        {/* Document Preview Modal */}
        <Modal 
          show={previewConfig.show} 
          onHide={() => setPreviewConfig({ ...previewConfig, show: false })} 
          size="lg" 
          centered
        >
          <Modal.Header closeButton>
            <Modal.Title>{previewConfig.title}</Modal.Title>
          </Modal.Header>
          <Modal.Body className="p-0 text-center bg-light">
            {previewConfig.url && (previewConfig.url.includes('pdf') || previewConfig.url.startsWith('data:application/pdf')) ? (
              <iframe 
                src={previewConfig.url} 
                title="Document Preview" 
                style={{ width: '100%', height: '70vh', border: 'none' }}
              />
            ) : (
              <img 
                src={previewConfig.url} 
                alt="Document Preview" 
                style={{ maxWidth: '100%', maxHeight: '70vh', objectFit: 'contain' }} 
              />
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setPreviewConfig({ ...previewConfig, show: false })}>
              Close
            </Button>
            <a href={previewConfig.url} download={`${previewConfig.title}.png`} className="btn btn-primary">
              <FaDownload className="me-2" /> Download
            </a>
          </Modal.Footer>
        </Modal>
      </div>
    </div>
  );
};

export default EmployeeProfilePage;
